import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterValue } from '../../components';
import { LastDateValue, useLastDate } from '../useLastDate';
import { useFilter } from './useFilter';

interface DateRangeFilterProps {
  filter: FilterValue[];
  allowReset?: boolean;
  showPastDates?: boolean;
  titleLocaliseKey?: string;
}

export const useDateRangeFilter = ({
  filter,
  allowReset = true,
  showPastDates = true,
  titleLocaliseKey = 'common.creation-date',
}: DateRangeFilterProps) => {
  const { t } = useTranslation();

  const { getGroupValue } = useFilter();

  const { getLastDate } = useLastDate();

  const defaultRangeValue = useMemo(
    () => (showPastDates ? LastDateValue.LAST_7_DAYS : LastDateValue.NEXT_7_DAYS),
    [showPastDates]
  );

  const customRangeValue = useMemo(() => {
    const dateRange = getGroupValue({ filter, groupId: 'date' });

    return {
      from: dateRange?.from ?? getLastDate(defaultRangeValue).from,
      to: dateRange?.to ?? getLastDate(defaultRangeValue).to,
    };
  }, [filter, getGroupValue, getLastDate, defaultRangeValue]);

  const pastDates = useMemo(
    () => [
      {
        id: LastDateValue.LAST_24_HRS,
        label: t('dateRangeFilter.LAST_24_HRS'),
        value: LastDateValue.LAST_24_HRS,
      },
      {
        id: LastDateValue.LAST_7_DAYS,
        label: t('dateRangeFilter.LAST_7_DAYS'),
        value: LastDateValue.LAST_7_DAYS,
      },
      {
        id: LastDateValue.LAST_30_DAYS,
        label: t('dateRangeFilter.LAST_30_DAYS'),
        value: LastDateValue.LAST_30_DAYS,
      },
      {
        id: LastDateValue.LAST_3_MONTHS,
        label: t('dateRangeFilter.LAST_3_MONTHS'),
        value: LastDateValue.LAST_3_MONTHS,
      },
      {
        id: LastDateValue.LAST_6_MONTHS,
        label: t('dateRangeFilter.LAST_6_MONTHS'),
        value: LastDateValue.LAST_6_MONTHS,
      },
      {
        id: LastDateValue.LAST_12_MONTHS,
        label: t('dateRangeFilter.LAST_12_MONTHS'),
        value: LastDateValue.LAST_12_MONTHS,
      },
      {
        id: LastDateValue.ALL_TIME,
        label: t('dateRangeFilter.ALL_TIME'),
        value: LastDateValue.ALL_TIME,
      },
    ],
    [t]
  );

  const futureDates = useMemo(
    () => [
      {
        id: LastDateValue.NEXT_24_HRS,
        label: t('dateRangeFilter.NEXT_24_HRS'),
        value: LastDateValue.NEXT_24_HRS,
      },
      {
        id: LastDateValue.NEXT_7_DAYS,
        label: t('dateRangeFilter.NEXT_7_DAYS'),
        value: LastDateValue.NEXT_7_DAYS,
      },
      {
        id: LastDateValue.NEXT_30_DAYS,
        label: t('dateRangeFilter.NEXT_30_DAYS'),
        value: LastDateValue.NEXT_30_DAYS,
      },
      {
        id: LastDateValue.NEXT_3_MONTHS,
        label: t('dateRangeFilter.NEXT_3_MONTHS'),
        value: LastDateValue.NEXT_3_MONTHS,
      },
      {
        id: LastDateValue.NEXT_6_MONTHS,
        label: t('dateRangeFilter.NEXT_6_MONTHS'),
        value: LastDateValue.NEXT_6_MONTHS,
      },
      {
        id: LastDateValue.NEXT_12_MONTHS,
        label: t('dateRangeFilter.NEXT_12_MONTHS'),
        value: LastDateValue.NEXT_12_MONTHS,
      },
    ],
    [t]
  );

  return {
    group: {
      id: 'date',
      title: t(titleLocaliseKey),
      allowReset,
      items: [
        {
          id: 'custom-range',
          label: t('dateRangeFilter.custom-range'),
          value: customRangeValue,
          dateRange: true,
        },
        ...(showPastDates ? pastDates : futureDates),
      ],
    },
  };
};
